/**
 * NotificationCenter — shows unseen changelog entries ("What's New")
 * Triggered from TopBar with an Inbox icon.
 * Replaces the old workspace-notification bell.
 */
import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01Icon as X, InboxIcon as Inbox, RefreshIcon as RefreshCw } from '@hugeicons/core-free-icons';
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuthFetch } from "@/hooks/useAuthFetch";

interface ChangelogEntry {
  id: number;
  title: string;
  version: string | null;
  content: string | null;
  image: string | null;
  link: string | null;
  type: string | null;
  published_at: string;
}

export function NotificationCenter() {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasUnseen, setHasUnseen] = useState(false);
  const authFetch = useAuthFetch();

  const fetchEntries = useCallback(async () => {
    const token = localStorage.getItem('genofx:token');
    if (!token) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await authFetch('/api/changelog/unseen');
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
        setHasUnseen(data.hasUnseen || false);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const dismiss = async (id: number) => {
    try {
      await authFetch(`/api/changelog/${id}/dismiss`, { method: 'POST' });
      const remaining = entries.filter(e => e.id !== id);
      setEntries(remaining);
      if (remaining.length === 0) setHasUnseen(false);
    } catch { /* silent */ }
  };

  const formatDate = (s: string) =>
    new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" title="What's New">
          <HugeiconsIcon icon={Inbox} className="h-5 w-5" />
          {hasUnseen && entries.length > 0 && (
            <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center bg-accent rounded-full text-[10px] font-bold">
              {entries.length > 9 ? '9+' : entries.length}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/40">
          <h4 className="font-semibold text-sm">What's New</h4>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={fetchEntries} title="Refresh">
            <HugeiconsIcon icon={RefreshCw} className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`}  />
          </Button>
        </div>

        <ScrollArea className="h-[320px]">
          {loading ? (
            <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
          ) : entries.length === 0 ? (
            <div className="p-8 text-center flex flex-col items-center gap-2">
              <HugeiconsIcon icon={Inbox} className="h-8 w-8 text-muted-foreground/20" />
              <p className="text-xs text-muted-foreground">All caught up!</p>
              <p className="text-[11px] text-muted-foreground/60">No new updates since your last visit.</p>
            </div>
          ) : (
            <div className="divide-y divide-border/20">
              {entries.map(entry => (
                <div key={entry.id} className="px-4 py-3.5 hover:bg-muted/20 transition-colors group relative">
                  {/* Dismiss button */}
                  <button
                    className="absolute top-2.5 right-2.5 h-5 w-5 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:bg-muted transition-all"
                    onClick={() => dismiss(entry.id)}
                    title="Mark as seen"
                  >
                    <HugeiconsIcon icon={X} className="h-3 w-3 text-muted-foreground"  />
                  </button>

                  {/* Version + type badges */}
                  {(entry.version || entry.type) && (
                    <div className="flex items-center gap-1.5 mb-1.5 pr-6">
                      {entry.version && (
                        <span className="text-[10px] font-bold bg-accent/10 text-accent border border-accent/20 px-1.5 py-0.5 rounded-full">
                          v{entry.version}
                        </span>
                      )}
                      {entry.type && (
                        <span className="text-[10px] text-muted-foreground capitalize bg-muted/50 px-1.5 py-0.5 rounded-full">
                          {entry.type}
                        </span>
                      )}
                    </div>
                  )}

                  <p className="text-[13px] font-semibold leading-snug pr-6">{entry.title}</p>

                  {entry.content && (
                    <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                      {entry.content.replace(/<[^>]+>/g, '')}
                    </p>
                  )}

                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[10px] text-muted-foreground/50">{formatDate(entry.published_at)}</p>
                    {entry.link && (
                      <a href={entry.link} target="_blank" rel="noopener" className="text-[10px] text-accent hover:underline">
                        Read more →
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="px-4 py-2 border-t border-border/20 flex items-center justify-between">
          <a href="https://genofx.com/changelog" className="text-[11px] text-accent hover:underline">View all updates →</a>
          {entries.length > 0 && (
            <button
              className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => entries.forEach(e => dismiss(e.id))}
            >
              Dismiss all
            </button>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
